/**
 * PropertyKeywords — cross-source per-keyword view.
 *
 * Anchor: union of GSC + Ahrefs + DFS keywords. Per-source columns
 * aligned, with "in_X" flags so disagreement is filterable. The
 * keyword universe lens — the daily-driver for keyword research +
 * cross-vendor index validation.
 */
import { useState } from "react";
import { useParams } from "react-router-dom";
import { Card } from "@tremor/react";
import {
  Tag, Search, ChevronUp, ChevronDown, Check, X, ExternalLink,
} from "lucide-react";
import {
  useProperty, usePropertyKeywords,
  type KeywordRow, type KeywordsLensFilters,
} from "@/lib/queries";
import { cn } from "@/lib/utils";


export default function PropertyKeywords() {
  const { propertyId: rawId } = useParams<{ propertyId: string }>();
  const propertyId = Number(rawId);
  const property = useProperty(propertyId);
  const [filters, setFilters] = useState<KeywordsLensFilters>({
    sort: "gsc_impressions", sort_dir: "desc", limit: 200, offset: 0,
  });
  const kws = usePropertyKeywords(propertyId, filters);

  const setF = (patch: Partial<KeywordsLensFilters>) =>
    setFilters((f) => ({ ...f, offset: 0, ...patch }));

  if (property.isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!property.data) return <div className="text-sm text-red-600">Property not found.</div>;
  const p = property.data;

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2">
          <Tag className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-semibold tracking-tight">Keywords</h1>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Cross-source keyword universe for <span className="font-mono">{p.domain}</span> —
          union of GSC + Ahrefs + DFS rankings. Filter by "only_disagreement"
          to find keywords where vendors disagree on whether you rank.
        </p>
      </div>

      {kws.data?.sources_present && (
        <div className="flex flex-wrap items-center gap-2 text-[11px]">
          <span className="text-muted-foreground">Joined sources:</span>
          <SrcChip label="GSC"     present={kws.data.sources_present.gsc} />
          <SrcChip label="Ahrefs"  present={kws.data.sources_present.ahrefs} />
          <SrcChip label="DataForSEO" present={kws.data.sources_present.dfs} />
        </div>
      )}

      <Card>
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <input type="text" placeholder="filter keyword by substring…"
                value={filters.search ?? ""}
                onChange={(e) => setF({ search: e.target.value || undefined })}
                className="w-full rounded-md border border-input bg-background pl-7 pr-2 py-1.5 text-xs" />
            </div>
            <SrcFilterBtn label="GSC only"
              active={!!filters.only_in_gsc}
              onClick={() => setF({ only_in_gsc: filters.only_in_gsc ? undefined : true })} />
            <SrcFilterBtn label="Ahrefs only"
              active={!!filters.only_in_ahrefs}
              onClick={() => setF({ only_in_ahrefs: filters.only_in_ahrefs ? undefined : true })} />
            <SrcFilterBtn label="DFS only"
              active={!!filters.only_in_dfs}
              onClick={() => setF({ only_in_dfs: filters.only_in_dfs ? undefined : true })} />
            <SrcFilterBtn label="Disagreement only"
              active={!!filters.only_disagreement}
              onClick={() => setF({ only_disagreement: filters.only_disagreement ? undefined : true })} />
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            {kws.data && <span>{kws.data.items.length.toLocaleString()} of {kws.data.total.toLocaleString()} keywords</span>}
            {Object.keys(filters).some((k) => !["sort", "sort_dir", "limit", "offset"].includes(k) && filters[k as keyof KeywordsLensFilters] !== undefined) && (
              <button type="button"
                onClick={() => setFilters({ sort: "gsc_impressions", sort_dir: "desc", limit: 200, offset: 0 })}
                className="text-primary hover:underline">Clear filters</button>
            )}
          </div>
        </div>
      </Card>

      <Card className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="border-b border-border text-left">
            <tr>
              <SortHeader label="Keyword"         col="keyword"         filters={filters} setF={setF} className="w-[24%]" />
              <SortHeader label="Sources"         col="keyword"         filters={filters} setF={setF} />
              <SortHeader label="GSC clicks"     col="gsc_clicks"      filters={filters} setF={setF} align="right" />
              <SortHeader label="GSC impr"       col="gsc_impressions" filters={filters} setF={setF} align="right" />
              <SortHeader label="GSC pos"        col="gsc_position"    filters={filters} setF={setF} align="right" />
              <SortHeader label="Ah pos"         col="ah_traffic"      filters={filters} setF={setF} align="right" />
              <SortHeader label="Ah vol"         col="ah_volume"       filters={filters} setF={setF} align="right" />
              <SortHeader label="Ah KD"          col="ah_volume"       filters={filters} setF={setF} align="right" />
              <SortHeader label="DFS pos"        col="dfs_volume"      filters={filters} setF={setF} align="right" />
              <SortHeader label="DFS vol"        col="dfs_volume"      filters={filters} setF={setF} align="right" />
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Top URL</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {kws.isLoading && <tr><td colSpan={11} className="px-2 py-4 text-center text-muted-foreground">Loading…</td></tr>}
            {kws.data?.items.map((kw) => <KwRow key={kw.keyword} kw={kw} />)}
            {kws.data && kws.data.items.length === 0 && (
              <tr><td colSpan={11} className="px-2 py-4 text-center text-muted-foreground">
                No keywords match the filters.
              </td></tr>
            )}
          </tbody>
        </table>

        {kws.data && kws.data.total > (filters.limit ?? 200) && (
          <div className="mt-3 flex items-center justify-between text-xs">
            <span className="text-muted-foreground">
              Page {Math.floor((filters.offset ?? 0) / (filters.limit ?? 200)) + 1} of{" "}
              {Math.ceil(kws.data.total / (filters.limit ?? 200))}
            </span>
            <div className="flex items-center gap-1">
              <button type="button"
                disabled={(filters.offset ?? 0) === 0}
                onClick={() => setFilters((f) => ({ ...f, offset: Math.max(0, (f.offset ?? 0) - (f.limit ?? 200)) }))}
                className="rounded-md border border-border bg-background px-2 py-1 hover:bg-accent disabled:opacity-50">Prev</button>
              <button type="button"
                disabled={(filters.offset ?? 0) + (filters.limit ?? 200) >= kws.data.total}
                onClick={() => setFilters((f) => ({ ...f, offset: (f.offset ?? 0) + (f.limit ?? 200) }))}
                className="rounded-md border border-border bg-background px-2 py-1 hover:bg-accent disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}


function KwRow({ kw }: { kw: KeywordRow }) {
  const topUrl = kw.ah_url || kw.dfs_url || "";
  return (
    <tr className="hover:bg-accent/40">
      <td className="px-2 py-1.5 align-top">
        <span className="line-clamp-2">{kw.keyword}</span>
      </td>
      <td className="px-2 py-1.5 align-top">
        <div className="flex items-center gap-0.5 text-[10px]">
          <SrcDot label="G" present={kw.in_gsc} title="Google Search Console" />
          <SrcDot label="A" present={kw.in_ahrefs} title="Ahrefs" />
          <SrcDot label="D" present={kw.in_dfs} title="DataForSEO" />
        </div>
      </td>
      <td className="px-2 py-1.5 text-right align-top">
        {kw.gsc_clicks != null && kw.gsc_clicks > 0 ? kw.gsc_clicks.toLocaleString() : "—"}
      </td>
      <td className="px-2 py-1.5 text-right align-top">
        {kw.gsc_impressions != null && kw.gsc_impressions > 0 ? kw.gsc_impressions.toLocaleString() : "—"}
      </td>
      <td className="px-2 py-1.5 text-right align-top">
        {kw.gsc_position != null && kw.gsc_position > 0 ? kw.gsc_position.toFixed(1) : "—"}
      </td>
      <td className="px-2 py-1.5 text-right align-top">
        {kw.ah_position ?? "—"}
      </td>
      <td className="px-2 py-1.5 text-right align-top">
        {kw.ah_volume?.toLocaleString() ?? "—"}
      </td>
      <td className="px-2 py-1.5 text-right align-top">
        {kw.ah_difficulty ?? "—"}
      </td>
      <td className="px-2 py-1.5 text-right align-top">
        {kw.dfs_position ?? "—"}
      </td>
      <td className="px-2 py-1.5 text-right align-top">
        {kw.dfs_volume?.toLocaleString() ?? "—"}
      </td>
      <td className="max-w-[24%] px-2 py-1.5 align-top font-mono text-[11px]">
        {topUrl ? (
          <a href={topUrl} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-0.5 hover:underline" title={topUrl}>
            <span className="truncate">{stripScheme(topUrl)}</span>
            <ExternalLink className="h-3 w-3 opacity-50" />
          </a>
        ) : "—"}
      </td>
    </tr>
  );
}


function SrcDot({ label, present, title }: { label: string; present: boolean; title: string }) {
  return (
    <span title={title}
      className={cn(
        "inline-flex h-4 w-4 items-center justify-center rounded font-mono",
        present ? "bg-emerald-500/20 text-emerald-700 dark:text-emerald-400" : "bg-muted text-muted-foreground/40",
      )}>
      {label}
    </span>
  );
}

function SrcChip({ label, present }: { label: string; present: boolean }) {
  return (
    <span className={cn(
      "inline-flex items-center gap-0.5 rounded px-2 py-0.5",
      present ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "bg-muted text-muted-foreground line-through",
    )}>
      {present ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
      {label}
    </span>
  );
}

function SrcFilterBtn({ label, active, onClick }: {
  label: string; active: boolean; onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick}
      className={cn(
        "rounded-md border px-2 py-1.5 text-xs",
        active ? "border-primary bg-primary/10 text-primary" : "border-input bg-background hover:bg-accent",
      )}>{label}</button>
  );
}

function SortHeader({ label, col, filters, setF, align, className }: {
  label: string; col: string;
  filters: KeywordsLensFilters;
  setF: (patch: Partial<KeywordsLensFilters>) => void;
  align?: "right"; className?: string;
}) {
  const active = filters.sort === col;
  const dir = active ? filters.sort_dir : "asc";
  return (
    <th onClick={() => setF({ sort: col, sort_dir: active && dir === "desc" ? "asc" : "desc", offset: 0 })}
      className={cn(
        "cursor-pointer select-none px-2 py-1.5 font-medium text-muted-foreground hover:text-foreground",
        align === "right" && "text-right", className,
      )}>
      <span className="inline-flex items-center gap-0.5">
        {label}
        {active && (dir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
      </span>
    </th>
  );
}

function stripScheme(url: string): string {
  return url.replace(/^https?:\/\//, "").replace(/^www\./, "");
}
